import type { LucideIcon } from 'lucide-react';
import { Facebook, Instagram, MessageCircle, ShoppingBag, Store, ShoppingCart } from 'lucide-react';

/**
 * Per-channel visual identity. Tailwind's JIT scanner needs *literal* class
 * strings to appear somewhere in source — it can't see `` `bg-channel-${key}` ``
 * template interpolation — so every class each channel needs is spelled out in
 * full here rather than assembled dynamically. Overall chrome (card/border/
 * muted) stays brand-neutral; only these tokens carry the platform's tint.
 */
export interface ChannelMeta {
  key: string;
  name: string;
  description: string;
  icon: LucideIcon;
  classes: {
    iconBg: string;
    iconText: string;
    border: string;
    dot: string;
    connectBtn: string;
    softBg: string;
    softBorder: string;
    softText: string;
  };
}

export const messagingChannelMeta: Record<'facebook' | 'instagram' | 'whatsapp', ChannelMeta> = {
  facebook: {
    key: 'facebook',
    name: 'Messenger',
    description: 'Auto-reply to Facebook Page messages',
    icon: Facebook,
    classes: {
      iconBg: 'bg-channel-messenger/10',
      iconText: 'text-channel-messenger',
      border: 'border-channel-messenger/20',
      dot: 'bg-channel-messenger',
      connectBtn: 'bg-channel-messenger hover:bg-channel-messenger/90 text-channel-messenger-foreground',
      softBg: 'bg-channel-messenger/5',
      softBorder: 'border-channel-messenger/15',
      softText: 'text-channel-messenger',
    },
  },
  instagram: {
    key: 'instagram',
    name: 'Instagram',
    description: 'Respond to Instagram DMs and comments',
    icon: Instagram,
    classes: {
      iconBg: 'bg-channel-instagram/10',
      iconText: 'text-channel-instagram',
      border: 'border-channel-instagram/20',
      dot: 'bg-channel-instagram',
      connectBtn: 'bg-channel-instagram hover:bg-channel-instagram/90 text-channel-instagram-foreground',
      softBg: 'bg-channel-instagram/5',
      softBorder: 'border-channel-instagram/15',
      softText: 'text-channel-instagram',
    },
  },
  whatsapp: {
    key: 'whatsapp',
    name: 'WhatsApp Business',
    description: 'Automated messaging via WhatsApp Business API',
    icon: MessageCircle,
    classes: {
      iconBg: 'bg-channel-whatsapp/10',
      iconText: 'text-channel-whatsapp',
      border: 'border-channel-whatsapp/20',
      dot: 'bg-channel-whatsapp',
      connectBtn: 'bg-channel-whatsapp hover:bg-channel-whatsapp/90 text-channel-whatsapp-foreground',
      softBg: 'bg-channel-whatsapp/5',
      softBorder: 'border-channel-whatsapp/15',
      softText: 'text-channel-whatsapp',
    },
  },
};

export const storeChannelMeta: Record<'shopify' | 'woocommerce' | 'amazon' | 'salla' | 'ebay' | 'lazada', ChannelMeta> = {
  shopify: {
    key: 'shopify',
    name: 'Shopify',
    description: 'Sync products & orders from your Shopify store',
    icon: ShoppingBag,
    classes: {
      iconBg: 'bg-channel-shopify/10',
      iconText: 'text-channel-shopify',
      border: 'border-channel-shopify/20',
      dot: 'bg-channel-shopify',
      connectBtn: 'bg-channel-shopify hover:bg-channel-shopify/90 text-channel-shopify-foreground',
      softBg: 'bg-channel-shopify/5',
      softBorder: 'border-channel-shopify/15',
      softText: 'text-channel-shopify',
    },
  },
  woocommerce: {
    key: 'woocommerce',
    name: 'WooCommerce',
    description: 'Sync products & orders from your WordPress store',
    icon: Store,
    classes: {
      iconBg: 'bg-channel-woocommerce/10',
      iconText: 'text-channel-woocommerce',
      border: 'border-channel-woocommerce/20',
      dot: 'bg-channel-woocommerce',
      connectBtn: 'bg-channel-woocommerce hover:bg-channel-woocommerce/90 text-channel-woocommerce-foreground',
      softBg: 'bg-channel-woocommerce/5',
      softBorder: 'border-channel-woocommerce/15',
      softText: 'text-channel-woocommerce',
    },
  },
  amazon: {
    key: 'amazon',
    name: 'Amazon Storefront',
    description: 'Sync ASIN catalog & FBA/FBM inventory via SP-API',
    icon: ShoppingCart,
    classes: {
      iconBg: 'bg-amber-500/10 dark:bg-amber-400/10',
      iconText: 'text-amber-600 dark:text-amber-400',
      border: 'border-amber-500/20 dark:border-amber-400/20',
      dot: 'bg-amber-500',
      connectBtn: 'bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-500 dark:hover:bg-amber-600',
      softBg: 'bg-amber-500/5',
      softBorder: 'border-amber-500/15',
      softText: 'text-amber-600 dark:text-amber-400',
    },
  },
  salla: {
    key: 'salla',
    name: 'Salla Store',
    description: 'Sync Gulf & Saudi storefront catalog via Merchant API v2',
    icon: Store,
    classes: {
      iconBg: 'bg-emerald-500/10 dark:bg-emerald-400/10',
      iconText: 'text-emerald-600 dark:text-emerald-400',
      border: 'border-emerald-500/20 dark:border-emerald-400/20',
      dot: 'bg-emerald-500',
      connectBtn: 'bg-emerald-600 hover:bg-emerald-700 text-white dark:bg-emerald-500 dark:hover:bg-emerald-600',
      softBg: 'bg-emerald-500/5',
      softBorder: 'border-emerald-500/15',
      softText: 'text-emerald-600 dark:text-emerald-400',
    },
  },
  ebay: {
    key: 'ebay',
    name: 'eBay Store',
    description: 'Sync eBay listings & inventory via Sell API',
    icon: ShoppingBag,
    classes: {
      iconBg: 'bg-sky-500/10 dark:bg-sky-400/10',
      iconText: 'text-sky-600 dark:text-sky-400',
      border: 'border-sky-500/20 dark:border-sky-400/20',
      dot: 'bg-sky-500',
      connectBtn: 'bg-sky-600 hover:bg-sky-700 text-white dark:bg-sky-500 dark:hover:bg-sky-600',
      softBg: 'bg-sky-500/5',
      softBorder: 'border-sky-500/15',
      softText: 'text-sky-600 dark:text-sky-400',
    },
  },
  lazada: {
    key: 'lazada',
    name: 'Lazada Store',
    description: 'Sync Southeast Asia storefront catalog & stock',
    icon: ShoppingBag,
    classes: {
      iconBg: 'bg-indigo-500/10 dark:bg-indigo-400/10',
      iconText: 'text-indigo-600 dark:text-indigo-400',
      border: 'border-indigo-500/20 dark:border-indigo-400/20',
      dot: 'bg-indigo-500',
      connectBtn: 'bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-500 dark:hover:bg-indigo-600',
      softBg: 'bg-indigo-500/5',
      softBorder: 'border-indigo-500/15',
      softText: 'text-indigo-600 dark:text-indigo-400',
    },
  },
};
